import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  catchError
} from 'rxjs/operators';
import { MultiSearchService } from '../../../core/services/search/search.service';
import { LanguageService } from '../../../core/services/language/language.service';
import { Utils } from '../../../shared/utils';

export interface NavSuggestionItem {
  id: number;
  media_type: 'movie' | 'tv' | 'person';
  title?: string;
  original_title?: string;
  name?: string;
  original_name?: string;
  release_date?: string;
  first_air_date?: string;
  poster_path?: string | null;
  profile_path?: string | null;
  known_for_department?: string;
  vote_average?: number;
}

@Component({
  selector: 'app-nav-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './nav-search.component.html',
  styleUrl: './nav-search.component.scss'
})
export class NavSearchComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  private readonly searchService = inject(MultiSearchService);
  public readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly utils = inject(Utils);
  private readonly elementRef = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();

  public isOpen = false;
  public searchControl = new FormControl('');
  public suggestions: NavSuggestionItem[] = [];
  public isLoading = false;
  public selectedIndex = -1;

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged((prev, curr) => (prev ?? '').trim() === (curr ?? '').trim()),
        switchMap((val) => {
          const rawQuery = (val ?? '').trim();
          if (rawQuery.length < 2) {
            this.suggestions = [];
            this.isLoading = false;
            this.selectedIndex = -1;
            return of(null);
          }

          this.isLoading = true;
          this.selectedIndex = -1;
          const query = this.utils.filterSearchQuery(rawQuery);
          const lang = this.languageService.currentLanguage?.code || 'en-US';

          return this.searchService.getSearchResult(query, lang).pipe(
            catchError((err) => {
              console.error('Navbar search error:', err);
              return of(null);
            })
          );
        })
      )
      .subscribe((res: any) => {
        this.isLoading = false;
        if (res && Array.isArray(res.results)) {
          this.suggestions = res.results.slice(0, 7);
        } else {
          this.suggestions = [];
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toggleSearch(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 50);
    } else {
      this.closeSearch();
    }
  }

  public closeSearch(): void {
    this.isOpen = false;
    this.searchControl.setValue('');
    this.suggestions = [];
    this.selectedIndex = -1;
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeSearch();
      return;
    }

    if (this.suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.selectedIndex = (this.selectedIndex + 1) % this.suggestions.length;
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.selectedIndex =
          this.selectedIndex <= 0 ? this.suggestions.length - 1 : this.selectedIndex - 1;
        return;
      }
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.selectedIndex >= 0 && this.selectedIndex < this.suggestions.length) {
        this.selectSuggestion(this.suggestions[this.selectedIndex]);
      } else {
        this.submitSearch();
      }
    }
  }

  public submitSearch(): void {
    const rawVal = (this.searchControl.value ?? '').trim();
    if (!rawVal) return;
    this.closeSearch();
    const searchTerm = this.utils.filterSearchQuery(rawVal).replace(/\s+/g, '+');
    this.router.navigate(['/search'], {
      queryParams: { query: searchTerm }
    });
  }

  public selectSuggestion(item: NavSuggestionItem): void {
    this.closeSearch();
    const title = this.getDisplayTitle(item);
    const slug = `${item.id}-${this.utils.movieNameSplitter(title)}`;
    this.router.navigate(['/', item.media_type, slug]);
  }

  public getDisplayTitle(item: NavSuggestionItem): string {
    return (
      item.title ||
      item.original_title ||
      item.name ||
      item.original_name ||
      'Untitled'
    );
  }

  public getPosterImage(item: NavSuggestionItem): string {
    const path = item.media_type === 'person' ? item.profile_path : item.poster_path;
    if (!path) {
      return '/assets/images/not_found_2_3.png';
    }
    return `https://image.tmdb.org/t/p/w92${path}`;
  }

  public getMetaInfo(item: NavSuggestionItem): string {
    if (item.media_type === 'person') {
      return item.known_for_department || 'Actor / Crew';
    }
    const dateStr = item.release_date || item.first_air_date;
    const year = this.utils.extractYearFromDate(dateStr);
    return year ? `${year}` : (dateStr || '');
  }

  public getMediaLabel(type: string): string {
    if (type === 'movie') return 'Movie';
    if (type === 'tv') return 'TV';
    return 'Person';
  }

  public onImageError(event: Event): void {
    this.utils.onImageVerticalError(event);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeSearch();
    }
  }
}
